import React from 'react';
import {
  PolicyContainer,
  PolicyHeader,
  PolicyTitle,
  EffectiveDate,
  PolicySection,
  PolicySectionTitle,
  PolicySubTitle,
  PolicyText,
  PolicyList,
  HighlightBox,
  ContactInfo,
  TableOfContents,
  TOCLink
} from './SharedStyledComponents';

const BeestmodePrivacyPolicyPage = () => {
  return (
    <PolicyContainer>
      <PolicyHeader>
        <PolicyTitle>מדיניות פרטיות</PolicyTitle>
        <EffectiveDate>עדכון אחרון: 7 בספטמבר 2026</EffectiveDate>
      </PolicyHeader>

      <PolicySection>
        <PolicySectionTitle>תוכן עניינים</PolicySectionTitle>
        <TableOfContents>
          <TOCLink href="#overview">סקירה כללית</TOCLink>
          <TOCLink href="#data-collection">איסוף מידע</TOCLink>
          <TOCLink href="#data-usage">שימוש במידע</TOCLink>
          <TOCLink href="#data-sharing">שיתוף מידע</TOCLink>
          <TOCLink href="#data-security">אבטחת מידע</TOCLink>
          <TOCLink href="#user-rights">זכויות המשתמש</TOCLink>
          <TOCLink href="#children-privacy">פרטיות קטינים</TOCLink>
          <TOCLink href="#international">משתמשים בינלאומיים</TOCLink>
          <TOCLink href="#cookies">עוגיות וטכנולוגיות מעקב</TOCLink>
          <TOCLink href="#data-retention">שמירת מידע</TOCLink>
          <TOCLink href="#third-party-services">שירותי צד שלישי</TOCLink>
          <TOCLink href="#app-specific">הרשאות ספציפיות לאפליקציה</TOCLink>
          <TOCLink href="#changes">שינויים במדיניות</TOCLink>
          <TOCLink href="#contact">יצירת קשר</TOCLink>
        </TableOfContents>
      </PolicySection>

      <PolicySection id="overview">
        <PolicySectionTitle>1. סקירה כללית</PolicySectionTitle>
        <PolicyText>
          האפליקציה שלנו מחויבת להגנה על פרטיותכם. מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלכם כאשר אתם משתמשים באפליקציה שלנו.
        </PolicyText>
        <PolicyText>
          מדיניות זו חלה על כל המידע שנאסף באמצעות האפליקציה ובאתרי Beestmode, לרבות דף הנחיתה להורדת האפליקציה.
        </PolicyText>
        <HighlightBox>
          <strong>חשוב:</strong> שימוש באפליקציה או באתר כפוף למדיניות זו. טכנולוגיות שיווק של Meta מופעלות בדף הנחיתה רק לאחר בחירה מפורשת שלכם.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="data-collection">
        <PolicySectionTitle>2. איסוף מידע</PolicySectionTitle>
        
        <PolicySubTitle>2.1 מידע שאתם מספקים</PolicySubTitle>
        <PolicyText>אנו עשויים לאסוף את המידע הבא שאתם מספקים במפורש:</PolicyText>
        <PolicyList>
          <li>פרטי הרשמה (שם, כתובת אימייל)</li>
          <li>מידע פרופיל (תמונה, העדפות)</li>
          <li>תוכן שאתם יוצרים או מעלים</li>
          <li>הודעות ופניות לתמיכה</li>
          <li>מידע תשלום (באמצעות מערכות תשלום מאובטחות)</li>
        </PolicyList>

        <PolicySubTitle>2.2 מידע שנאסף אוטומטית</PolicySubTitle>
        <PolicyText>האפליקציה עשויה לאסוף מידע טכני באופן אוטומטי:</PolicyText>
        <PolicyList>
          <li>מידע מכשיר (דגם, מערכת הפעלה, גרסת אפליקציה)</li>
          <li>מידע רשת (כתובת IP, ספק שירות)</li>
          <li>נתוני שימוש (זמן שימוש, פעולות באפליקציה)</li>
          <li>מידע מיקום (בהסכמה מפורשת בלבד)</li>
          <li>מזהים ייחודיים (כגון מזהה מכשיר או ספק; מזהה הפרסום IDFA זמין רק לאחר אישור הרשאת המעקב של Apple)</li>
        </PolicyList>

        <PolicySubTitle>2.3 מידע מצד שלישי</PolicySubTitle>
        <PolicyText>אנו עשויים לקבל מידע מצדדים שלישיים:</PolicyText>
        <PolicyList>
          <li>רשתות חברתיות (בעת התחברות דרכן)</li>
          <li>שירותי אנליטיקה (Google Analytics, Firebase)</li>
          <li>שירותי פרסום (מזהים לפרסום מותאם)</li>
          <li>Meta App Events (אירועי אפליקציה ומידע טכני לצורך מדידת התקנות וייחוס פרסום)</li>
          <li>שירותי תשלום (מידע על עסקאות)</li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="data-usage">
        <PolicySectionTitle>3. שימוש במידע</PolicySectionTitle>
        <PolicyText>אנו משתמשים במידע שנאסף למטרות הבאות:</PolicyText>

        <PolicySubTitle>3.1 מטרות עיקריות</PolicySubTitle>
        <PolicyList>
          <li>מתן השירות ותפעול האפליקציה</li>
          <li>יצירת וניהול חשבון משתמש</li>
          <li>עיבוד תשלומים ומנויים</li>
          <li>מתן תמיכה טכנית</li>
          <li>שיפור ופיתוח האפליקציה</li>
        </PolicyList>

        <PolicySubTitle>3.2 מטרות משניות</PolicySubTitle>
        <PolicyList>
          <li>שליחת התראות ועדכונים</li>
          <li>מחקר ואנליטיקה</li>
          <li>אבטחה ומניעת הונאות</li>
          <li>פרסום מותאם (בהסכמה)</li>
          <li>עמידה בדרישות חוקיות</li>
        </PolicyList>

        <HighlightBox>
          <strong>הערה:</strong> אנו לא נשתמש במידע שלכם למטרות שאינן מפורטות במדיניות זו ללא הסכמתכם המפורשת.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="data-sharing">
        <PolicySectionTitle>4. שיתוף מידע</PolicySectionTitle>
        <PolicyText>אנו לא מוכרים או משכירים מידע אישי לצדדים שלישיים. עם זאת, אנו עשויים לשתף מידע במצבים הבאים:</PolicyText>

        <PolicySubTitle>4.1 ספקי שירות</PolicySubTitle>
        <PolicyList>
          <li>שירותי אירוח ענן (AWS, Google Cloud)</li>
          <li>שירותי אנליטיקה (Firebase Analytics)</li>
          <li>Meta Platforms (דיווח אירועי אפליקציה, מדידת קמפיינים וייחוס פרסום)</li>
          <li>שירותי תשלום (Apple Pay, Stripe)</li>
          <li>שירותי התראות (Apple Push Notifications)</li>
          <li>שירותי תמיכה טכנית</li>
        </PolicyList>

        <PolicySubTitle>4.2 דרישות חוקיות</PolicySubTitle>
        <PolicyText>אנו עשויים לחשוף מידע כאשר:</PolicyText>
        <PolicyList>
          <li>נדרש על פי חוק או צו בית משפט</li>
          <li>הגנה על זכויותינו המשפטיות</li>
          <li>חקירת הפרות אבטחה</li>
          <li>הגנה על בטיחות המשתמשים</li>
        </PolicyList>

        <PolicySubTitle>4.3 עסקאות עסקיות</PolicySubTitle>
        <PolicyText>במקרה של מיזוג, רכישה או מכירת נכסים, המידע עשוי להיות מועבר לגורם הרוכש.</PolicyText>
      </PolicySection>

      <PolicySection id="data-security">
        <PolicySectionTitle>5. אבטחת מידע</PolicySectionTitle>
        <PolicyText>אנו מיישמים אמצעי אבטחה מתקדמים להגנה על המידע שלכם:</PolicyText>

        <PolicySubTitle>5.1 אמצעי אבטחה טכניים</PolicySubTitle>
        <PolicyList>
          <li>הצפנה מתקדמת (AES-256) לנתונים רגישים</li>
          <li>תקשורת מאובטחת (HTTPS/TLS)</li>
          <li>אימות דו-שלבי</li>
          <li>ניטור מתמיד לאיתור איומים</li>
          <li>גיבויים מוצפנים</li>
        </PolicyList>

        <PolicySubTitle>5.2 אמצעי אבטחה ארגוניים</PolicySubTitle>
        <PolicyList>
          <li>הגבלת גישה לנתונים לעובדים מורשים בלבד</li>
          <li>הכשרות אבטחת מידע לעובדים</li>
          <li>ביקורות אבטחה שוטפות</li>
          <li>מדיניות סיסמאות חזקה</li>
        </PolicyList>

        <PolicySubTitle>5.3 אחסון נתונים</PolicySubTitle>
        <PolicyText>
          הנתונים שלכם מאוחסנים בשרתים מאובטחים במרכזי נתונים מוסמכים. אנו משתמשים רק בספקי אירוח העומדים בתקני אבטחה בינלאומיים.
        </PolicyText>
      </PolicySection>

      <PolicySection id="user-rights">
        <PolicySectionTitle>6. זכויות המשתמש</PolicySectionTitle>
        <PolicyText>יש לכם מספר זכויות ביחס למידע האישי שלכם:</PolicyText>

        <PolicySubTitle>6.1 זכות עיון</PolicySubTitle>
        <PolicyText>אתם רשאים לבקש עותק של כל המידע האישי שלכם שנמצא ברשותנו.</PolicyText>

        <PolicySubTitle>6.2 זכות תיקון</PolicySubTitle>
        <PolicyText>אתם רשאים לבקש לתקן מידע שגוי או לא מדויק.</PolicyText>

        <PolicySubTitle>6.3 זכות מחיקה</PolicySubTitle>
        <PolicyText>אתם רשאים לבקש מחיקת המידע האישי שלכם, כפוף לדרישות חוקיות.</PolicyText>

        <PolicySubTitle>6.4 זכות הגבלת עיבוד</PolicySubTitle>
        <PolicyText>אתם רשאים לבקש הגבלת עיבוד המידע במצבים מסוימים.</PolicyText>

        <PolicySubTitle>6.5 זכות נישאות נתונים</PolicySubTitle>
        <PolicyText>אתם רשאים לבקש העברת המידע שלכם לספק אחר.</PolicyText>

        <PolicySubTitle>6.6 זכות התנגדות</PolicySubTitle>
        <PolicyText>אתם רשאים להתנגד לעיבוד מידע למטרות פרסום או שיווק.</PolicyText>

        <HighlightBox>
          <strong>איך מממשים זכויות:</strong> לממש את הזכויות שלכם, פנו אלינו באמצעות פרטי הקשר שבסוף המדיניות.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="children-privacy">
        <PolicySectionTitle>7. פרטיות קטינים</PolicySectionTitle>
        <PolicyText>
          האפליקציה שלנו מיועדת למשתמשים בגילאי 13 ומעלה. אנו לא אוספים במודע מידע אישי מילדים מתחת לגיל 13.
        </PolicyText>
        <PolicyText>
          אם הובא לידיעתנו שאספנו מידע מילד מתחת לגיל 13, נמחק את המידע מיד מהמערכות שלנו.
        </PolicyText>
        <PolicyText>
          הורים או אפוטרופוסים שיש להם חשד שילד מתחת לגיל 13 סיפק מידע אישי, מוזמנים לפנות אלינו מיד.
        </PolicyText>
      </PolicySection>

      <PolicySection id="international">
        <PolicySectionTitle>8. משתמשים בינלאומיים</PolicySectionTitle>
        <PolicyText>
          האפליקציה זמינה למשתמשים בינלאומיים, ואנו מחויבים לעמוד בתקנות הפרטיות הבינלאומיות.
        </PolicyText>

        <PolicySubTitle>8.1 GDPR (תקנת הגנת המידע הכללית באירופה)</PolicySubTitle>
        <PolicyText>למשתמשים באיחוד האירופי יש זכויות נוספות לפי ה-GDPR:</PolicyText>
        <PolicyList>
          <li>זכות לקבלת הסכמה מפורשת לעיבוד מידע</li>
          <li>זכות למחיקת מידע ("זכות להישכח")</li>
          <li>זכות לנישאות נתונים</li>
          <li>זכות להגיש תלונה לרשות הפיקוח</li>
        </PolicyList>

        <PolicySubTitle>8.2 CCPA (חוק פרטיות הצרכן בקליפורניה)</PolicySubTitle>
        <PolicyText>תושבי קליפורניה זכאים ל:</PolicyText>
        <PolicyList>
          <li>לדעת איזה מידע אישי נאסף</li>
          <li>לדעת אם המידע נמכר או נחשף</li>
          <li>לסרב למכירת מידע אישי</li>
          <li>לגשת למידע האישי</li>
          <li>לזכות שוויון בשירות ובמחיר</li>
        </PolicyList>

        <PolicySubTitle>8.3 העברת נתונים בינלאומית</PolicySubTitle>
        <PolicyText>
          נתונים עשויים להיות מועברים ומעובדים במדינות שונות. אנו מבטיחים שההעברה תתבצע בהתאם לתקנות הפרטיות הרלוונטיות.
        </PolicyText>
      </PolicySection>

      <PolicySection id="cookies">
        <PolicySectionTitle>9. עוגיות וטכנולוגיות מעקב</PolicySectionTitle>
        <PolicyText>
          האפליקציה משתמשת בטכנולוגיות שונות לשיפור החוויה ולמטרות אנליטיות.
        </PolicyText>

        <PolicySubTitle>9.1 סוגי טכנולוגיות</PolicySubTitle>
        <PolicyList>
          <li>מזהים ייחודיים של מכשיר</li>
          <li>מזהי פרסום (IDFA ב-iOS)</li>
          <li>נתוני אנליטיקה (Firebase Analytics)</li>
          <li>פיקסלי מעקב לפרסום</li>
        </PolicyList>

        <PolicySubTitle>9.2 מטרות השימוש</PolicySubTitle>
        <PolicyList>
          <li>שיפור ביצועי האפליקציה</li>
          <li>הבנת דפוסי שימוש</li>
          <li>פרסום מותאם אישית</li>
          <li>מניעת הונאות</li>
          <li>תמיכה טכנית</li>
        </PolicyList>

        <PolicySubTitle>9.3 שליטה בטכנולוגיות מעקב</PolicySubTitle>
        <PolicyText>אתם יכולים לשלוט בטכנולוגיות מעקב באמצעות:</PolicyText>
        <PolicyList>
          <li>הגדרות iOS תחת ״פרטיות ואבטחה״ &gt; ״מעקב״</li>
          <li>אישור או דחייה של בקשת שקיפות המעקב של Apple ‏(App Tracking Transparency)</li>
          <li>ביטול הסכמה לאנליטיקה</li>
        </PolicyList>

        <PolicySubTitle>9.4 דף הנחיתה, אנליטיקה פרטית ו-Meta Pixel</PolicySubTitle>
        <PolicyText>
          בדף הנחיתה להורדת Beestmode אנו משתמשים במזהה דפדפן מקומי לצורך האנליטיקה הפרטית שלנו, כדי להבחין בין ביקור ראשון לביקור חוזר. בעת ביקור עשויים להישלח לשרת האנליטיקה שלנו זמן מקומי, סוג מכשיר, קוד הבאנר, פעולות בדף ומיקום משוער רק אם הוא כבר זמין ושיתופו הותר — איננו מבקשים הרשאת מיקום מהדף.
        </PolicyText>
        <PolicyText>
          Meta Pixel חסום כברירת מחדל ומופעל רק לאחר לחיצה על "אישור" בהודעת עוגיות השיווק. לאחר אישור אנו שולחים ל-Meta אירועי PageView ו-BeestmodeLandingView, קוד הבאנר וסוג המכשיר שזוהה, לצורך מדידת קמפיינים, יצירת קהלים מותאמים והצגת פרסום חוזר. Meta עשויה לקבל גם את כתובת ה-IP, כתובת העמוד, פרטי הדפדפן והמכשיר ומזהי Meta או עוגיות בהתאם למדיניותה.
        </PolicyText>
        <PolicyText>
          מדף הנחיתה איננו שולחים ל-Meta את מזהה המבקר הפרטי שלנו, מיקום מדויק, או מידע בריאותי, אימונים ופעילות מתוך האפליקציה. ניתן לדחות או לבטל את ההסכמה בכל עת באמצעות "הגדרות עוגיות" בתחתית דף הנחיתה; הבחירה אינה משפיעה על אפשרות הורדת האפליקציה. השימוש של האפליקציה ב-Meta SDK מתואר בנפרד בסעיף הבא.
        </PolicyText>

        <PolicySubTitle>9.5 ‏Meta SDK באפליקציית iOS</PolicySubTitle>
        <PolicyText>
          אפליקציית iOS כוללת את Facebook SDK Core של Meta. ה-SDK שולח אירועי מחזור חיים של האפליקציה, כגון הפעלה או פתיחה, ועשוי לשלוח מידע על התקנת האפליקציה, פרטי מכשיר ואפליקציה, מזהי מכשיר ומידע אבחוני או אנליטי אחר. אנו משתמשים במידע זה למדידת קמפיינים והתקנות, ייחוס פרסום, אנליטיקה, תפעול האפליקציה ואבחון תקלות. איננו מגדירים אירועים הכוללים מידע בריאותי, תוכן אימונים או תוכן שהמשתמש יצר להעברה ל-Meta.
        </PolicyText>
        <PolicyText>
          ב-iOS 14.5 ומעלה, האפליקציה מבקשת את הרשאת שקיפות המעקב של Apple לפני הפעלת מעקב מפרסמים או גישה למזהה הפרסום IDFA. אם תדחו את הבקשה, האפליקציה לא תעמיד את ה-IDFA לרשות Meta לצורך מעקב בין אפליקציות ואתרים של חברות אחרות, והפונקציות המרכזיות באפליקציה יישארו זמינות. גם ללא הרשאה, עשויים להישלח ל-Meta אירועים ומידע טכני מוגבלים שאינם מבוססי IDFA, וייתכן שיתקבל מידע ייחוס מצטבר באמצעות SKAdNetwork של Apple, בהתאם לכללי הפלטפורמה.
        </PolicyText>
        <PolicyText>
          ניתן לשנות את הרשאת המעקב בכל עת בהגדרות iOS תחת ״פרטיות ואבטחה״ &gt; ״מעקב״. Meta מעבדת את המידע שהיא מקבלת בהתאם ל<a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noreferrer">מדיניות הפרטיות של Meta</a>.
        </PolicyText>
      </PolicySection>

      <PolicySection id="data-retention">
        <PolicySectionTitle>10. שמירת מידע</PolicySectionTitle>
        <PolicyText>
          אנו שומרים על המידע שלכם כל עוד זה נחוץ למטרות שצוינו במדיניות זו.
        </PolicyText>

        <PolicySubTitle>10.1 תקופות שמירה</PolicySubTitle>
        <PolicyList>
          <li>מידע חשבון: עד מחיקת החשבון</li>
          <li>נתוני שימוש: 24 חודשים</li>
          <li>לוגי אבטחה: 12 חודשים</li>
          <li>מידע תמיכה: 36 חודשים</li>
          <li>נתונים פיננסיים: 7 שנים (דרישת חוק)</li>
        </PolicyList>

        <PolicySubTitle>10.2 מחיקת מידע</PolicySubTitle>
        <PolicyText>
          בתום תקופת השמירה או לבקשתכם, המידע יימחק באופן מאובטח מכל המערכות שלנו.
        </PolicyText>
      </PolicySection>

      <PolicySection id="third-party-services">
        <PolicySectionTitle>11. שירותי צד שלישי</PolicySectionTitle>
        <PolicyText>האפליקציה משתמשת בשירותי צד שלישי הבאים:</PolicyText>

        <PolicySubTitle>11.1 שירותי Apple</PolicySubTitle>
        <PolicyList>
          <li>Apple App Store (הורדות ועדכונים)</li>
          <li>Apple Pay (תשלומים)</li>
          <li>Apple Push Notifications (התראות)</li>
          <li>iCloud (גיבוי נתונים - אופציונלי)</li>
        </PolicyList>

        <PolicySubTitle>11.2 שירותי Google</PolicySubTitle>
        <PolicyList>
          <li>Firebase Analytics (אנליטיקה)</li>
          <li>Firebase Crashlytics (דיווח קריסות)</li>
          <li>Google Cloud Platform (אירוח)</li>
        </PolicyList>

        <PolicySubTitle>11.3 Meta</PolicySubTitle>
        <PolicyList>
          <li>Meta Pixel (מדידת קמפיינים, קהלים מותאמים ופרסום חוזר — בהסכמה)</li>
          <li>Facebook SDK Core ו-Meta App Events (מדידת התקנות וקמפיינים, ייחוס פרסום, אנליטיקה ותפעול האפליקציה)</li>
        </PolicyList>

        <PolicySubTitle>11.4 שירותים נוספים</PolicySubTitle>
        <PolicyList>
          <li>מערכות תשלום (Stripe, PayPal)</li>
          <li>שירותי אימייל (SendGrid)</li>
          <li>שירותי CDN (CloudFlare)</li>
        </PolicyList>

        <PolicyText>
          כל שירות צד שלישי כפוף למדיניות הפרטיות שלו. אנו ממליצים לעיין במדיניות אלו.
        </PolicyText>
      </PolicySection>

      <PolicySection id="app-specific">
        <PolicySectionTitle>12. הרשאות ספציפיות לאפליקציה</PolicySectionTitle>
        <PolicyText>האפליקציה עשויה לבקש הרשאות הבאות:</PolicyText>

        <PolicySubTitle>12.1 הרשאות נדרשות</PolicySubTitle>
        <PolicyList>
          <li><strong>אינטרנט:</strong> לתקשורת עם השרתים</li>
          <li><strong>אחסון:</strong> לשמירת נתונים מקומיים</li>
          <li><strong>התראות:</strong> לשליחת עדכונים</li>
        </PolicyList>

        <PolicySubTitle>12.2 הרשאות אופציונליות</PolicySubTitle>
        <PolicyList>
          <li><strong>מיקום:</strong> לשירותים מבוססי מיקום (בהסכמה)</li>
          <li><strong>מצלמה:</strong> לצילום או סריקה (בהסכמה)</li>
          <li><strong>אנשי קשר:</strong> לשיתוף עם חברים (בהסכמה)</li>
          <li><strong>קלינדר:</strong> לתזכורות ואירועים (בהסכמה)</li>
        </PolicyList>

        <PolicyText>
          אתם יכולים לשנות הרשאות בכל עת דרך הגדרות iOS של המכשיר.
        </PolicyText>
      </PolicySection>

      <PolicySection id="changes">
        <PolicySectionTitle>13. שינויים במדיניות</PolicySectionTitle>
        <PolicyText>
          אנו עשויים לעדכן מדיניות פרטיות זו מעת לעת. שינויים יהיו בתוקף מרגע פרסומם באפליקציה.
        </PolicyText>

        <PolicySubTitle>13.1 הודעה על שינויים</PolicySubTitle>
        <PolicyList>
          <li>התראה באפליקציה עבור שינויים מהותיים</li>
          <li>אימייל למשתמשים רשומים</li>
          <li>עדכון תאריך "עדכון אחרון" בראש המדיניות</li>
        </PolicyList>

        <PolicySubTitle>13.2 סוגי שינויים</PolicySubTitle>
        <PolicyText><strong>שינויים מהותיים</strong> - ידרשו הסכמה מחודשת:</PolicyText>
        <PolicyList>
          <li>שינוי מטרות איסוף המידע</li>
          <li>הוספת צדדים שלישיים חדשים</li>
          <li>שינוי תקופות שמירת מידע</li>
        </PolicyList>

        <PolicyText><strong>שינויים טכניים</strong> - ייכנסו לתוקף ללא הסכמה נוספת:</PolicyText>
        <PolicyList>
          <li>עדכון פרטי קשר</li>
          <li>הבהרות טקסט</li>
          <li>תיקוני שגיאות</li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="contact">
        <PolicySectionTitle>14. יצירת קשר</PolicySectionTitle>
        <PolicyText>
          לכל שאלה בנוגע למדיניות פרטיות זו או לטיפול במידע האישי שלכם, תוכלו לפנות אלינו:
        </PolicyText>

        <ContactInfo>
          <PolicySubTitle>פרטי קשר</PolicySubTitle>
          <PolicyText><strong>אימייל:</strong> haggaimagen87@gmail.com</PolicyText>
          <PolicyText><strong>טלפון:</strong> 050-5456644</PolicyText>
          <PolicyText><strong>זמני מענה:</strong> ראשון - חמישי, 9:00-17:00</PolicyText>
        </ContactInfo>

        <PolicySubTitle>14.1 טיפול בפניות</PolicySubTitle>
        <PolicyText>אנו מתחייבים לטפל בכל פנייה תוך:</PolicyText>
        <PolicyList>
          <li>אישור קבלה: תוך 24 שעות</li>
          <li>מענה ראשוני: תוך 72 שעות</li>
          <li>טיפול מלא: תוך 30 יום</li>
        </PolicyList>

        <PolicySubTitle>14.2 נציב הגנת הפרטיות</PolicySubTitle>
        <PolicyText>
          אם אינכם מרוצים מהטיפול בפנייתכם, תוכלו לפנות לנציב הגנת הפרטיות בישראל:
        </PolicyText>
        <PolicyText>
          <strong>אתר:</strong> www.gov.il/privacy<br/>
          <strong>טלפון:</strong> 02-6662222
        </PolicyText>
      </PolicySection>

      <PolicySection>
        <HighlightBox>
          <PolicyText style={{marginBottom: 0}}>
            <strong>תאריך עדכון אחרון:</strong> 7 בספטמבר 2026<br/>
            <strong>גרסת מדיניות:</strong> 2.3<br/>
            <strong>שפה מקורית:</strong> עברית
          </PolicyText>
        </HighlightBox>
      </PolicySection>
    </PolicyContainer>
  );
};

export default BeestmodePrivacyPolicyPage;
